<template>
  <div class="select-page">
    <div class="select-card">
      <div class="select-logo">
        <img src="@/assets/images/logo-salesflow.png" alt="SalesFlow" class="select-logo-img" />
      </div>

      <div class="select-header">
        <h2 class="select-title">Informe sua empresa</h2>
        <p class="select-subtitle">
          Digite o identificador da sua empresa para acessar o portal.
        </p>
      </div>

      <b-form class="select-form" @submit.prevent="continuar">
        <div class="form-group-custom">
          <label class="form-label-custom" for="empresa-slug">Empresa</label>
          <div class="input-wrapper">
            <feather-icon icon="LayersIcon" class="input-icon" />
            <b-form-input
              id="empresa-slug"
              ref="slugInput"
              v-model="slug"
              class="form-input-custom"
              placeholder="ex: dkp"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              autofocus
              :state="erro ? false : null"
              @input="onInput"
            />
          </div>
          <small v-if="erro" class="text-danger help-text">
            {{ erro }}
          </small>
          <small v-else class="text-muted help-text">
            Identificador da sua empresa. Em caso de dúvida, contate o suporte.
          </small>
        </div>

        <b-button
          type="submit"
          variant="primary"
          class="select-button"
          block
          :disabled="!slug || carregando"
        >
          <b-spinner v-if="carregando" small class="mr-2" />
          {{ carregando ? 'Verificando...' : 'Continuar' }}
          <feather-icon v-if="!carregando" icon="ArrowRightIcon" class="ml-2" />
        </b-button>
      </b-form>

      <div class="select-footer">
        <img src="@/assets/images/logo-salesflow.png" alt="SalesFlow" class="footer-logo-img" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BForm, BFormInput, BButton, BSpinner,
} from 'bootstrap-vue';
import axios from '@/libs/axios';
import { isUserLoggedIn } from '@/auth/utils';
import { resetTenantColors } from '@/utils/tenantTheme';

// Valores que NÃO são tenants reais (podem ter poluído o localStorage via /home etc.)
const RESERVED_SLUGS = ['home', 'login', 'second-page', 'error-404', 'not-authorized'];

export default {
  components: {
    BForm,
    BFormInput,
    BButton,
    BSpinner,
  },
  data() {
    return {
      slug: '',
      erro: '',
      carregando: false,
    };
  },
  mounted() {
    // Visual neutro nesta página (sem cores de tenant)
    resetTenantColors();

    // O campo SEMPRE começa vazio. Nunca pré-preenchemos a partir do localStorage:
    // um valor inválido salvo antes (ex.: "homacmillan") criava um loop ao ser
    // re-submetido / editado por cima.
    this.slug = '';

    // Ignora valores reservados (ex.: "home") que possam ter poluído o localStorage
    const raw = localStorage.getItem('tenantSlug');
    const savedSlug = raw && !RESERVED_SLUGS.includes(raw) ? raw : null;

    // Atalho: se já existe contexto de tenant e o usuário está logado, vai direto pra home.
    if (savedSlug && isUserLoggedIn()) {
      this.$router.replace({ name: 'home', params: { tenantSlug: savedSlug } });
    }
  },
  methods: {
    onInput() {
      this.slug = (this.slug || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
      this.erro = '';
    },
    async continuar() {
      const slug = (this.slug || '').toLowerCase().trim();
      if (!slug || this.carregando) return;

      this.carregando = true;
      this.erro = '';
      try {
        // Valida que a empresa existe ANTES de navegar/persistir, para que um slug
        // inválido nunca polua o localStorage nem leve a uma URL quebrada.
        await axios.get(`/tenants/public/${slug}`);
        localStorage.setItem('tenantSlug', slug);
        this.$router.push({ name: 'login', params: { tenantSlug: slug } });
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          this.erro = `Empresa "${slug}" não encontrada. Verifique o identificador.`;
        } else {
          this.erro = 'Não foi possível verificar a empresa. Tente novamente.';
        }
        this.carregando = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.select-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #01094e 0%, #0575b5 100%);
  padding: 1.5rem;
}

.select-card {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(1, 9, 78, 0.25);
  padding: 2.5rem 2rem;
}

.select-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.select-logo-img {
  width: 200px;
  height: auto;
}

.select-header {
  text-align: center;
  margin-bottom: 1.75rem;
}

.select-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #01094e;
  margin-bottom: 0.4rem;
}

.select-subtitle {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
}

.form-group-custom {
  margin-bottom: 1.5rem;
}

.form-label-custom {
  display: block;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 0.9rem;
  color: #94a3b8;
  width: 18px;
  height: 18px;
}

.form-input-custom {
  height: 48px;
  padding-left: 2.6rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
}

.help-text {
  font-size: 0.78rem;
  margin-top: 4px;
  display: block;
}

.select-button {
  height: 48px;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.select-footer {
  text-align: center;
  margin-top: 2rem;
}

.footer-logo-img {
  width: 120px;
  height: auto;
  opacity: 0.6;
}
</style>
